import { BusinessCase, SUPPORTED_UNITS } from '@schuettflix/interfaces';
import { CustomProduct } from '@/shared/forms/CustomProductsForm/schemas/getCustomProductsFormSchema.ts';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { productTypeToFreightTypeMap } from '@/shared/domain/product/productTypeToFreightTypeMap.ts';
import { productTypeToDisposalPurposeMap } from '@/shared/domain/product/productTypeToDisposalPurposeMap.ts';
import { MARKET_CODE_TO_CURRENCY_CODE_MAP, MarketCode } from '@schuettflix/utils';
import { productTypeToUnitMap } from '@/shared/domain/product/productTypeToUnitMap.ts';

type CreateDefaultCustomProductsParams = {
    businessCase: BusinessCase;
    marketCode: MarketCode;
    defaultTaxClass?: string;
    defaultBillingUnit?: SUPPORTED_UNITS;
};

export function generateBusinessCaseCustomProducts({
    businessCase,
    marketCode,
    defaultTaxClass,
    defaultBillingUnit,
}: CreateDefaultCustomProductsParams) {
    const baseProduct = createBaseProduct({ businessCase, marketCode, defaultTaxClass, defaultBillingUnit });
    const transportProduct = createTransportProduct({ businessCase, marketCode, defaultTaxClass, defaultBillingUnit });

    switch (businessCase.pricingStrategy) {
        case 'FRANCO':
        case 'MERCHANT': {
            return [baseProduct, transportProduct];
        }
        case 'STANDARD': {
            if (businessCase.isStandalone) {
                return [createStandaloneProduct({ businessCase, marketCode, defaultTaxClass, defaultBillingUnit })];
            }

            return [baseProduct, transportProduct];
        }
    }
}

function createBaseProduct({
    businessCase,
    marketCode,
    defaultTaxClass,
    defaultBillingUnit,
}: CreateDefaultCustomProductsParams) {
    return {
        productType: businessCase.productType,
        category: getProductCategoryByProductType(businessCase.productType),
        currencyCode: MARKET_CODE_TO_CURRENCY_CODE_MAP[marketCode],
        unit: defaultBillingUnit ?? productTypeToUnitMap[businessCase.productType],
        purchaseTaxClassId: defaultTaxClass,
        salesTaxClassId: defaultTaxClass,
        showZeroPurchasePrice: false,
        showZeroSalesPrice: false,
    } as CustomProduct;
}

function createTransportProduct({
    businessCase,
    marketCode,
    defaultTaxClass,
    defaultBillingUnit,
}: CreateDefaultCustomProductsParams) {
    const isCustomProductType = businessCase.productType.includes('CUSTOM_');
    const isMerchantCase = businessCase.pricingStrategy === 'MERCHANT';

    return {
        productType: isCustomProductType ? 'CUSTOM_TRANSPORT' : 'TRANSPORT',
        category: 'TRANSPORT',
        unit: defaultBillingUnit ?? productTypeToUnitMap[businessCase.productType],
        freightType: productTypeToFreightTypeMap[businessCase.productType],
        disposalPurpose: productTypeToDisposalPurposeMap[businessCase.productType],
        currencyCode: MARKET_CODE_TO_CURRENCY_CODE_MAP[marketCode],
        purchaseTaxClassId: defaultTaxClass,
        salesTaxClassId: defaultTaxClass,
        showZeroPurchasePrice: false,
        showZeroSalesPrice: false,
        ...(isMerchantCase && {
            purchasePrice: 0,
            salesPrice: 0,
        }),
    } as CustomProduct;
}

function createStandaloneProduct({
    businessCase,
    marketCode,
    defaultTaxClass,
    defaultBillingUnit,
}: CreateDefaultCustomProductsParams) {
    const isTransportProduct =
        businessCase.productType === 'TRANSPORT' || businessCase.productType === 'CUSTOM_TRANSPORT';

    return {
        productType: businessCase.productType,
        disposalPurpose: productTypeToDisposalPurposeMap[businessCase.productType],
        currencyCode: MARKET_CODE_TO_CURRENCY_CODE_MAP[marketCode],
        category: getProductCategoryByProductType(businessCase.productType),
        unit: defaultBillingUnit ?? productTypeToUnitMap[businessCase.productType],
        purchaseTaxClassId: defaultTaxClass,
        salesTaxClassId: defaultTaxClass,
        showZeroPurchasePrice: false,
        showZeroSalesPrice: false,
        ...(isTransportProduct && {
            freightType: productTypeToFreightTypeMap[businessCase.productType],
        }),
    } as CustomProduct;
}
